import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaCalendarAlt, FaTag, FaDollarSign, FaEye, FaImage } from 'react-icons/fa';
import { BsCheckLg } from 'react-icons/bs';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

const cookingImage = "https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80";
const defaultPlanImage = "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80";

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem 0;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #291759, #858CD3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
  background: white;
  border-radius: 50px;
  padding: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 2rem auto;
  overflow-x: auto;
`;

const Tab = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #291759, #858CD3)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#666'};
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #291759, #858CD3)' : 'rgba(133, 140, 211, 0.1)'};
    color: ${props => props.active ? 'white' : '#291759'};
  }
`;

const MainSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 3rem 0;
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const ContentColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ImageColumn = styled.div`
  flex: 1;
`;

const FeatureImage = styled.img`
  width: 100%;
  height: 350px;
  border-radius: 15px;
  object-fit: cover;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  color: #291759;
  font-weight: 700;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CheckIcon = styled.div`
  color: #291759;
  font-size: 1.25rem;
  margin-top: 0.2rem;
`;

const FeatureText = styled.p`
  margin: 0;
  color: #555;
  line-height: 1.6;
`;

const PlansSection = styled.section`
  margin: 3rem 0;
`;

const PlansHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const PlansCount = styled.span`
  background: linear-gradient(135deg, #291759, #858CD3);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const PlanCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
`;

const PlanImage = styled.div`
  height: 140px;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : `url(${defaultPlanImage})`};
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.3));
  }
`;

const PlanImagePlaceholder = styled.div`
  height: 140px;
  background: linear-gradient(135deg, #f0f2ff, #e8ebff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #858CD3;
  font-size: 2rem;
`;

const PlanContent = styled.div`
  padding: 1.5rem;
`;

const PlanHeader = styled.div`
  margin-bottom: 1rem;
`;

const PlanCategory = styled.span`
  background: linear-gradient(135deg, #291759, #858CD3);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.75rem;
  text-transform: capitalize;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const PlanName = styled.h3`
  font-size: 1.3rem;
  margin: 0.8rem 0 0.5rem 0;
  color: #291759;
  font-weight: 700;
  line-height: 1.3;
`;

const PlanPrice = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #291759;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const PlanDescription = styled.p`
  color: #666;
  margin: 1rem 0;
  line-height: 1.5;
  font-size: 0.9rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PlanMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 1rem 0;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #666;
  font-size: 0.85rem;
`;

const ViewDetailsButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.8rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  position: relative;
  height: 200px;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : `url(${defaultPlanImage})`};
  background-size: cover;
  background-position: center;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.5));
  }
`;

const ModalHeaderPlaceholder = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #291759, #858CD3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  color: #291759;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

const ModalCategory = styled.span`
  background: linear-gradient(135deg, #291759, #858CD3);
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 15px;
  font-size: 0.8rem;
  text-transform: capitalize;
  font-weight: 600;
  margin-bottom: 1rem;
  display: inline-block;
`;

const ModalPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #291759;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const ModalDescription = styled.p`
  color: #666;
  line-height: 1.7;
  margin: 1.5rem 0;
  font-size: 1rem;
`;

const ModalMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1.5rem 0;
  padding: 1.5rem;
  background: #f8f9ff;
  border-radius: 15px;
`;

const ModalMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #555;
  font-weight: 500;
`;

const FeaturesSection = styled.div`
  margin: 2rem 0;
`;

const FeaturesTitle = styled.h3`
  font-size: 1.3rem;
  color: #291759;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureListItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.8rem;
  color: #555;
  font-size: 0.95rem;
  
  svg {
    color: #291759;
    flex-shrink: 0;
  }
`;

const SelectPlanButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #291759, #858CD3);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1.2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
  }
`;

const NoPlans = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 20px;
  margin: 2rem 0;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  
  &:after {
    content: '';
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #291759;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AllPlans = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, [activeTab]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError('');
      
      let plansQuery;
      
      if (activeTab === 'all') {
        plansQuery = collection(db, 'plans');
      } else {
        plansQuery = query(
          collection(db, 'plans'),
          where('category', '==', activeTab)
        );
      }
      
      const querySnapshot = await getDocs(plansQuery);
      const plansList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      plansList.sort((a, b) => (b.preference || 0) - (a.preference || 0));
      console.log('Fetched plans:', plansList);
      setPlans(plansList);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (plan) => {
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleSelectPlan = (planId) => {
    localStorage.setItem('selectedPlanId', planId);
    navigate('/auth/signup', { state: { selectedPlanId: planId } });
    setShowModal(false);
  };

  const formatCategory = (category) => {
    switch(category) {
      case 'diet': return 'Diet Plans';
      case 'workout': return 'Workout Plans';
      case 'bundle': return 'Bundle Plans';
      default: return 'Plans';
    }
  };

  const getDefaultFeatures = (category) => {
    switch(category) {
      case 'diet':
        return [
          'Personalized meal plans',
          'Nutritional guidance',
          'Progress tracking',
          'Mobile app access'
        ];
      case 'workout':
        return [
          'Custom workout routines',
          'Exercise demonstrations',
          'Progress monitoring',
          'Expert support'
        ];
      case 'bundle':
        return [
          'Combined diet & workout plans',
          'Comprehensive health tracking',
          'Premium support',
          'All-in-one solution'
        ];
      default:
        return [
          'Professional guidance',
          'Progress tracking',
          'Mobile app access',
          'Expert support'
        ];
    }
  };

  return (
    <PageContainer>
      <Header>
        <Title>All Plans</Title>
        <Subtitle>
          Comprehensive programs designed to optimize your health journey through nutrition and exercise
        </Subtitle>
      </Header>
      
      <TabsContainer>
        <Tab 
          active={activeTab === 'all'} 
          onClick={() => setActiveTab('all')}
        >
          All Plans
        </Tab>
        <Tab 
          active={activeTab === 'diet'} 
          onClick={() => setActiveTab('diet')}
        >
          Diet Plans
        </Tab>
        <Tab 
          active={activeTab === 'workout'} 
          onClick={() => setActiveTab('workout')}
        >
          Workout Plans
        </Tab>
        <Tab 
          active={activeTab === 'bundle'} 
          onClick={() => setActiveTab('bundle')}
        >
          Bundles
        </Tab>
      </TabsContainer>
      
      <MainSection>
        <ContentColumn>
          <SectionTitle>Holistic Health Programs</SectionTitle>
          <p style={{ fontSize: '1.1rem', color: '#555', lineHeight: '1.7', marginBottom: '2rem' }}>
            Our comprehensive plans combine nutrition and exercise strategies tailored to your unique biology, helping you achieve optimal health and wellness.
          </p>
          
          <div style={{ marginTop: '1.5rem' }}>
            <FeatureItem>
              <CheckIcon>
                <BsCheckLg />
              </CheckIcon>
              <FeatureText>Personalized meal and exercise recommendations</FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <CheckIcon>
                <BsCheckLg />
              </CheckIcon>
              <FeatureText>Expert guidance from certified Wonder Kliniker and trainers</FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <CheckIcon>
                <BsCheckLg />
              </CheckIcon>
              <FeatureText>Seamless integration with the GeoDiet app</FeatureText>
            </FeatureItem>
          </div>
        </ContentColumn>
        
        <ImageColumn>
          <FeatureImage src={cookingImage} alt="Healthy lifestyle" />
        </ImageColumn>
      </MainSection>
      
      <PlansSection>
        <PlansHeader>
          <SectionTitle style={{ margin: 0 }}>
            {formatCategory(activeTab)}
          </SectionTitle>
          {plans.length > 0 && (
            <PlansCount>{plans.length} plans available</PlansCount>
          )}
        </PlansHeader>
        
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <NoPlans>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>{error}</p>
          </NoPlans>
        ) : plans.length === 0 ? (
          <NoPlans>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              No {formatCategory(activeTab).toLowerCase()} available at the moment. Please check back later.
            </p>
          </NoPlans>
        ) : (
          <PlansGrid>
            {plans.map(plan => (
              <PlanCard key={plan.id} onClick={() => handleViewDetails(plan)}>
                {plan.imageUrl ? (
                  <PlanImage imageUrl={plan.imageUrl} />
                ) : (
                  <PlanImagePlaceholder>
                    <FaImage />
                  </PlanImagePlaceholder>
                )}
                
                <PlanContent>
                  <PlanHeader>
                    <PlanCategory>{plan.category}</PlanCategory>
                    <PlanName>{plan.name}</PlanName>
                    <PlanPrice>
                      <FaDollarSign size={16} />
                      {plan.price}
                    </PlanPrice>
                  </PlanHeader>
                  
                  <PlanDescription>{plan.description}</PlanDescription>
                  
                  <PlanMeta>
                    <MetaItem>
                      <FaCalendarAlt />
                      {plan.duration} days
                    </MetaItem>
                    <MetaItem>
                      <FaTag />
                      {plan.category}
                    </MetaItem>
                  </PlanMeta>
                  
                  <ViewDetailsButton onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(plan);
                  }}>
                    <FaEye />
                    View Details
                  </ViewDetailsButton>
                </PlanContent>
              </PlanCard>
            ))}
          </PlansGrid>
        )}
      </PlansSection>

      {/* Modal */}
      {showModal && selectedPlan && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowModal(false)}>
              <FaTimes />
            </CloseButton>
            
            {selectedPlan.imageUrl ? (
              <ModalHeader imageUrl={selectedPlan.imageUrl} />
            ) : (
              <ModalHeaderPlaceholder>
                <FaImage />
              </ModalHeaderPlaceholder>
            )}
            
            <ModalBody>
              <ModalCategory>{selectedPlan.category}</ModalCategory>
              <ModalTitle>{selectedPlan.name}</ModalTitle>
              <ModalPrice>
                <FaDollarSign />
                {selectedPlan.price}
              </ModalPrice>
              
              <ModalDescription>{selectedPlan.description}</ModalDescription>
              
              <ModalMetaGrid>
                <ModalMetaItem>
                  <FaCalendarAlt />
                  Duration: {selectedPlan.duration} days
                </ModalMetaItem>
                <ModalMetaItem>
                  <FaTag />
                  Category: {selectedPlan.category}
                </ModalMetaItem>
                <ModalMetaItem>
                  <FaDollarSign />
                  Price: ${selectedPlan.price}
                </ModalMetaItem>
                <ModalMetaItem>
                  <FaCheck />
                  Status: Active
                </ModalMetaItem>
              </ModalMetaGrid>
              
              <FeaturesSection>
                <FeaturesTitle>What's Included</FeaturesTitle>
                <FeaturesList>
                  {(selectedPlan.features && selectedPlan.features.length > 0 
                    ? selectedPlan.features 
                    : getDefaultFeatures(selectedPlan.category)
                  ).map((feature, index) => (
                    <FeatureListItem key={index}>
                      <FaCheck size={14} />
                      {feature}
                    </FeatureListItem>
                  ))}
                </FeaturesList>
              </FeaturesSection>
              
              <SelectPlanButton onClick={() => handleSelectPlan(selectedPlan.id)}>
                Select This Plan
              </SelectPlanButton>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default AllPlans;